import { useState } from "react";
import type { SupplierOrderReceiptResource } from "@exonn/js-sdk";
import { ModalV3 } from "src/components/ModalV3/ModalV3.tsx";
import { Button } from "src/components/Button/Button.tsx";
import { Input } from "src/components/Input/Input.tsx";
import { Flex } from "src/components/Flex.tsx";
import { useResourceFormTranslation } from "src/components/Form/hooks/useResourceFormTranslation.ts";
import { useGlobalSettings } from "src/hooks/use-global-settings.ts";

type Props = {
  receipt: SupplierOrderReceiptResource;
  isPending: boolean;
  onSubmit: (body: {
    quantity?: string;
    purchasing_price?: string;
    reason: string;
  }) => void;
  onClose: () => void;
};

// Исправление прихода: количество (0 — сторно) и/или закупочная цена в стандартной валюте.
export const CorrectReceiptDialog = ({
  receipt,
  isPending,
  onSubmit,
  onClose,
}: Props) => {
  const { t } = useResourceFormTranslation();
  const {
    data: { default_currency },
  } = useGlobalSettings();
  const [quantity, setQuantity] = useState(String(Number(receipt.amount)));
  const [price, setPrice] = useState(
    String(Number(receipt.purchasing_price ?? 0)),
  );
  const [reason, setReason] = useState("");

  const quantityChanged =
    quantity !== "" && Number(quantity) !== Number(receipt.amount);
  const priceChanged =
    price !== "" && Number(price) !== Number(receipt.purchasing_price ?? 0);

  return (
    <ModalV3 onClose={onClose}>
      <ModalV3.Positioner>
        <ModalV3.Content>
          <ModalV3.CloseTrigger />
          <ModalV3.Header>
            <ModalV3.Title>{t("receipt.correct.title")}</ModalV3.Title>
          </ModalV3.Header>
          <ModalV3.Body style={{ width: 460 }}>
            <Flex flexDirection={"column"} gap={"15px"}>
              <Input
                label={t("receipt.correct.quantity")}
                helperText={t("receipt.correct.quantity-helper")}
                type={"number"}
                min={0}
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
              />
              <Input
                label={t("receipt.correct.price", {
                  currency: default_currency?.iso,
                })}
                helperText={t("receipt.correct.price-helper")}
                type={"number"}
                min={0}
                value={price}
                onChange={(event) => setPrice(event.target.value)}
              />
              <Input
                label={t("dialog.reason")}
                value={reason}
                maxLength={255}
                onChange={(event) => setReason(event.target.value)}
              />
            </Flex>
          </ModalV3.Body>
          <ModalV3.Footer>
            <ModalV3.FooterButtons>
              <Button variant={"outlined"} onClick={onClose}>
                {t("dialog.cancel")}
              </Button>
              <Button
                variant={"filled"}
                disabled={
                  isPending ||
                  reason.trim() === "" ||
                  (!quantityChanged && !priceChanged)
                }
                onClick={() =>
                  onSubmit({
                    ...(quantityChanged ? { quantity } : {}),
                    ...(priceChanged ? { purchasing_price: price } : {}),
                    reason: reason.trim(),
                  })
                }
              >
                {t("receipt.correct.save")}
              </Button>
            </ModalV3.FooterButtons>
          </ModalV3.Footer>
        </ModalV3.Content>
      </ModalV3.Positioner>
    </ModalV3>
  );
};
